import {
  first,
  isEqual,
  last,
  reverse,
  cloneDeep,
  join,
  upperFirst,
  lowerFirst,
  groupBy
} from 'lodash';

import {
  calculateTime,
  getMarkWord,
  findMarkWords,
  copyToClipboard,
  isLetterUpperCase,
  cloneSubtitle,
  wait
} from '../functions';

import { getColorByShortcut } from '../../../../../../utils/colors';
import { takeClipboardRead, writeClipboard } from '../clipboard';

// ***************************************************************************************
// onBackspace supprime un caractère en arrière
// ***************************************************************************************
export async function onBackspace() {
  if (this.underSelection) {
    this.onDeleteSelection('onBackspace');
    return;
  }

  const currentLine = this.subtitle.lines[this.currentLineIndex];
  const currentWord = currentLine?.words[this.currentWordIndex];

  if (!currentWord) {
    if (this.currentLineIndex === 0) {
      return;
    }

    if (!currentLine.words.length) {
      // Faire l'historique
      this.addNewActionForUndo('onBackspace');

      // on supprime la ligne
      this.deleteLine();
      this.onRefreshCanvas();
    }

    this.currentLineIndex--;

    const lastWord = last(this.subtitle.lines[this.currentLineIndex].words);
    if (lastWord) {
      this.currentWordIndex = lastWord.indexWord;
      this.currentLetterIndex =
        lastWord.letters && lastWord.letters.length
          ? last(lastWord.letters).indexLetter
          : 0;
    } else {
      this.currentWordIndex = 0;
      this.currentLetterIndex = 0;
    }

    return;
  }

  // Faire l'historique
  this.addNewActionForUndo('onBackspace');

  
  if (!currentWord.text.length || currentWord.text === ' ') {
    // on supprime le mot
    currentLine.words.splice(currentWord.index, 1);

    if (currentWord.index === 0) {

      this.currentWordIndex = 0;
      this.currentLetterIndex = 0;
      
      this.onChange();
      return;
    }

    this.currentWordIndex--;
    this.currentLetterIndex--;

    this.onChange();
    return;
  }
  

  const { index, text, letters } = currentWord;

  if ((!letters || !letters.length) && text !== ' ') {
    console.warn('a t-on besoin de cette fonction ?');

    this.onRefreshCanvas();
    await wait(10);

    await this.onBackspace();

    return;
  }

  const currentIndex = letters.findIndex(
    (l) => l.indexLetter === this.currentLetterIndex
  );

  // Si le caractère précédent est un espace alors on supprime l'espace et on attache deux mots en un mot
  if (currentIndex === 0) {
    const prevWord = currentLine.words[index - 1];
    if (prevWord) {
      prevWord.text += text;
      prevWord.out = currentWord.out;

      currentLine.words = currentLine.words.filter((x) => x.index !== index);
      this.currentWordIndex--;
      this.currentLetterIndex--;
    } else {
      // Aller à la ligne précédente
      const prevLine = this.subtitle.lines[this.currentLineIndex - 1];
      if (prevLine) {
        const maxLength = this.getMaxLength(prevLine);

        // Si on dépasse X caractères, on ne peut pas aller à la ligne précédente
        if (prevLine.len + text.length > maxLength) {
          return;
        }

        const lastWord = last(prevLine.words);

        prevLine.words.push(currentWord);
        currentLine.words.splice(index, 1);

        this.currentLineIndex--;
        if (lastWord) {
          this.currentWordIndex = lastWord.indexWord;
          this.currentLetterIndex = last(lastWord.letters).indexLetter;
        } else {
          this.currentWordIndex = 0;
          this.currentLetterIndex = 0;
        }

        const sentence = this.getSentence(prevLine);
        prevLine.len = sentence.length;
      }
    }

    this.onChange();
    return;
  }

  // s'il reste un caractère alors on supprime le mot
  if (text.length === -1) { // ici j'annule le regroupement s'il ne reste rien
    currentLine.words = currentLine.words.filter((x) => x.index !== index);

    this.currentWordIndex--;
    this.currentLetterIndex--;
  } else {
    currentWord.text =
      text.substring(0, currentIndex - 1) + text.substring(currentIndex);

    this.currentLetterIndex--;

    // si la dernière lettre du mot est un point alors on remet la lettre suivante au minuscule
    const lastLetter = text[text.length - 1];
    if (lastLetter === '.' || lastLetter === "?" || lastLetter == "!") {
      const nextWord = this.getNextWord();
      if (nextWord && isLetterUpperCase(nextWord.text[0])) {
        nextWord.text = lowerFirst(nextWord.text);
      }
    }
    else {
      const prevLetter = text[this.currentLetter.index - 1];
       if (prevLetter === '.' || prevLetter === "?" || prevLetter == "!") {
        currentWord.text =
          currentWord.text.substring(0, this.currentLetter.index - 1) +
          this.currentLetter.letter.toLowerCase() +
          currentWord.text.substring(this.currentLetter.index);
      }
    }
  }

  this.onChange();
}

// ***************************************************************************************
// onEnter via la touche Entrée permet d'aller à la ligne
// ***************************************************************************************
export function onEnter() {
  if (this.underSelection) {
    this.onDeleteSelection('onEnter');
    return;
  }

  // Faire l'historique
  this.addNewActionForUndo('onEnter');

  let nextLine = this.subtitle.lines[this.currentLineIndex + 1];
  if (!nextLine) {
    const newLine = this.createNewLine(this.subtitle, this.currentLine);
    if (!newLine) {
      this.cancelUndo();
      return;
    }


    this.subtitle.lines.push(newLine);
    nextLine = newLine;
  }

  if (this.isLastLetterOfLine()) {
    this.currentLineIndex++;
    this.onChange();

    return;
  }

  const currentSentence = this.getCurrentSentence().substring(
    this.currentLetterIndex
  );
  // Si on dépasse X caractères, on ne peut pas mettre les mots suivants sur la prochaine ligne
  const maxLength = this.getMaxLength(this.currentLine);
  if (currentSentence.length + nextLine.len > maxLength) {
    this.cancelUndo();
    return;
  }

  const currentIndex = this.currentWord.letters.findIndex(
    (l) => l.indexLetter === this.currentLetterIndex
  );

  if (currentIndex === -1) {
    this.cancelUndo();
    return;
  }

  let nextWord;



  if (currentIndex === 0) {
    nextWord = this.currentWord;
  } else {
    if (currentIndex === this.currentWord.text.length) {
      nextWord = this.currentLine.words[this.currentWordIndex + 1];
    } else {
      // couper le mot en deux et le mot suivant va à la ligne
      const textWord1 = this.currentWord.text.substring(0, currentIndex);
      const textWord2 = this.currentWord.text.substring(currentIndex);

      this.currentWord.text = textWord1;

      if (textWord2 === '') {
        nextWord = this.currentLine.words[this.currentWordIndex + 1];
      } else {
        // Sinon on split le mot en deux
        const inOutTime = calculateTime(
          this.currentWord.in,
          this.currentWord.out,
          this.currentWord.text,
          textWord1
        );

        nextWord = {
          index: this.currentWord.index + 1,
          color: this.currentWord.color,
          italic: this.currentWord.italic,
          text: textWord2,
          in: inOutTime,
          out: this.currentWord.out,
        };

        this.currentWord.out = inOutTime;

        this.currentLine.words.splice(this.currentWordIndex + 1, 0, nextWord);
      }
    }
  }

  if (!nextWord) {
    this.cancelUndo();
    return;
  }
  
  const index = this.currentLine.words.findIndex((w) => isEqual(w, nextWord));
  const wordsToEnter = [...this.currentLine.words.slice(index)];

  for (let w of reverse(wordsToEnter)) {
    this.currentLine.words.pop();
    nextLine.words.unshift(w);
  }

  this.currentLineIndex++;
  this.currentWordIndex = 0;
  this.currentLetterIndex = 0;

  this.onChange();
}

// ***************************************************************************************
// onDelete permet de supprimer un caractère devant
// ***************************************************************************************
export function onDelete() {
  if (this.underSelection) {
    this.onDeleteSelection('onDelete');
    return;
  }

  const word = this.currentWord;

  // si c'est vide ou c'est la dernière lettre de la ligne,
  // alors récupérer le prochain mot ou la phrase de la ligne suivante
  if (this.isLastLetterOfLine()) {
    const nextLine = this.subtitle.lines[this.currentLineIndex + 1];

    if (!nextLine || !nextLine.words.length) {
      return;
    }

    // Faire l'historique
    this.addNewActionForUndo('onDelete');

    const nextWords = [...nextLine.words];
    for (let w of nextWords) {
      // Si on dépasse X caractères, on ne peut pas mettre le mot suivant sur la ligne
      const currentSentence = this.getCurrentSentence();
      const maxLength = this.getMaxLength(this.currentLine);
      if (currentSentence.length + w.text.length > maxLength) {
        break;
      }

      this.currentLine.words.push(w);
      nextLine.words.splice(0, 1);

      if (!nextLine.words.length) {
        this.subtitle.lines.splice(nextLine.index, 1);
      }
    }

    this.onChange();

    return;
  }

  // Faire l'historique
  this.addNewActionForUndo('onDelete');

  const { index, text, letters } = word;
  const currentIndex = letters?.findIndex(
    (l) => l.indexLetter === this.currentLetterIndex
  );

  const lastIndex = letters.length ? last(letters).index : -1;

  // si c'est la dernière lettre du mot, alors on supprime l'espace
  if (lastIndex === currentIndex) {
    const nextWord = this.currentLine.words[index + 1];

    if (nextWord) {
      word.text += nextWord.text;
      word.out = nextWord.out;

      this.currentLine.words.splice(this.currentWord.index + 1, 1);

      this.onChange();

      return;
    }
  }

  word.text =
    text.substring(0, currentIndex) + text.substring(currentIndex + 1);

  // Si le mot est vide alors on le supprime
  if (word.text.length === 0) {
    const nextWord = this.currentLine.words[index + 1];

    if (nextWord) {
      nextWord.in = word.in;
    } else {
      const lastWord = this.currentLine.words[index - 1];
      if (lastWord) {
        lastWord.out = word.out;

        this.currentWordIndex--;
        this.currentLetter--;
      }
    }

    this.currentLine.words.splice(index, 1);
  }

  this.onChange();
}

// ***************************************************************************************
// onSetColor applique la couleur à partir du mot en cours jusqu'à la fin de la phrase ou toute la phrase
// ou applique la couleur sur la sélection
// ***************************************************************************************
export function onSetColor(key) {
  const color = getColorByShortcut(key);

  if (!color) {
    return;
  }

  // Appliquer la couleur dans la sélection
  if (this.underSelection) {
    // Faire l'historique
    this.addNewActionForUndo('onSetColor');

    this.wordsToSelect.forEach((word) => {
      word.color = color.hex;
    });
    this.onChange();
    return;
  }

  const words = this.getWords();
  if(!words.length) {
    return;
  }

  const wordsColored = words.filter((x) => x.color === color.hex);

  if (words.length === wordsColored.length) {
    return;
  }

  // Faire l'historique
  this.addNewActionForUndo('onSetColor');

  const allColor = this.currentWord ? words
    .filter((w) => w.indexLine === this.currentWord.indexLine)
    .every((x) => x.color === color.hex) : true;

  words.forEach((word) => {
    // on applique la couleur sur toute la phrase
    if (allColor) {
      word.color = color.hex;
    } else {
      // On applique la couleur sur toute la ligne
      if (word.indexLine === this.currentWord.indexLine) {
        word.color = color.hex;
      }
    }
  });

  this.onChange();
}

// ***************************************************************************************
// On sélectionne les mots avec les touches clavier
// ***************************************************************************************
let selectCurrentIndex = 0;
let selectStartIndex = 0;
export function onSelect(key) {
  const words = this.getWords();

  if(!words.length){
    return;
  }

  if (this.underSelection) {
    if (key === 'SELECTLEFT' || key === 'SELECTTOP') {
      if (selectCurrentIndex === 0) {
        return;
      }
    }

    if (key === 'SELECTRIGHT' || key === 'SELECTBOTTOM') {
      if (selectCurrentIndex === last(words).indexWord) {
        return;
      }
    }
  }

  if (key === 'SELECTLEFT' || key === 'SELECTRIGHT') {
    this.onSelectLeftOrRight(key, words);
  } else {
    this.onSelectTopOrBottom(key, words);
  }
}

export function onSelectLeftOrRight(key, words) {
  if (!this.underSelection || !this.wordsToSelect.length) {
    this.underSelection = true;
    this.wordsToSelect = [];
    this.lettersToSelect = [];

    if (this.currentWord) {
      this.wordsToSelect.push(this.currentWord);

      selectStartIndex = this.currentWord.indexWord;
      selectCurrentIndex = this.currentWord.indexWord;
    } else {
      if (key === 'SELECTLEFT') {
        const prevWords = words.filter(
          (x) => x.indexLine === this.currentLineIndex - 1
        );
        if (!prevWords.length) {
          return;
        }

        const lastWord = last(prevWords);
        if (!lastWord) {
          return;
        }

        selectStartIndex = lastWord.indexWord;
        selectCurrentIndex = lastWord.indexWord;

        this.wordsToSelect.push(lastWord);
      } else {
        const nextWords = words.filter(
          (x) => x.indexLine === this.currentLineIndex + 1
        );
        if (!nextWords.length) {
          return;
        }

        const firstWord = first(nextWords);
        if (!firstWord) {
          return;
        }

        selectStartIndex = firstWord.indexWord;
        selectCurrentIndex = firstWord.indexWord;

        this.wordsToSelect.push(firstWord);
      }
    }

    this.onRefreshCanvas();
    return;
  }

  if (selectCurrentIndex === selectStartIndex) {
    const firstWord = first(this.wordsToSelect);
    const lastWord = last(this.wordsToSelect);

    if (firstWord.indexWord === selectCurrentIndex) {
      selectCurrentIndex = lastWord.indexWord;
    } else {
      selectCurrentIndex = firstWord.indexWord;
    }
  }

  let shouldRemove = false;
  if (key === 'SELECTLEFT') {
    shouldRemove = selectCurrentIndex > selectStartIndex;
  } else {
    shouldRemove = selectCurrentIndex < selectStartIndex;
  }

  if (shouldRemove) {
    if (key === 'SELECTLEFT') {
      this.wordsToSelect.pop();
    } else {
      this.wordsToSelect.shift();
    }

    if (!this.wordsToSelect.length) {
      this.underSelection = false;
      return;
    }

    if (selectStartIndex < selectCurrentIndex) {
      const lastWord = last(this.wordsToSelect);
      selectCurrentIndex = lastWord.indexWord;
    } else {
      const firstWord = first(this.wordsToSelect);
      selectCurrentIndex = firstWord.indexWord;
    }

    this.onRefreshCanvas();
    return;
  }

  if (key === 'SELECTLEFT') {
    selectCurrentIndex--;
  } else {
    selectCurrentIndex++;
  }

  const word = words[selectCurrentIndex];
  if (!word) {
    return;
  }

  if (key === 'SELECTLEFT') {
    this.wordsToSelect.unshift(word);
  } else {
    this.wordsToSelect.push(word);
  }

  selectCurrentIndex = word.indexWord;
  this.onRefreshCanvas();
}

export function onSelectTopOrBottom(key, words) {
  if (!this.underSelection) {
    this.underSelection = true;
    this.wordsToSelect = [];
    this.lettersToSelect = [];
  }
  if (!this.wordsToSelect.length) {
    if (this.currentWord) {
      this.wordsToSelect.push(this.currentWord);

      selectStartIndex = this.currentWord.indexWord;
      selectCurrentIndex = this.currentWord.indexWord;
    } else {
      const startIndex = this.currentWordIndex;
      let startLineIndex = this.currentLineIndex;
      let endLineIndex = this.currentLineIndex;

      this.wordsToSelect = getWordsToSelect(
        words,
        startIndex,
        startLineIndex,
        endLineIndex,
        key === 'SELECTDOWN'
      );

      if (this.wordsToSelect.length) {
        if (key === 'SELECTUP') {
          selectCurrentIndex = first(this.wordsToSelect).indexWord;
        } else {
          selectCurrentIndex = last(this.wordsToSelect).indexWord;
        }

        selectStartIndex = startIndex;
      } else {
        if (key === 'SELECTUP') {
          const prevWords = words.filter(
            (x) => x.indexLine === this.currentLineIndex - 1
          );
          if (!prevWords.length) {
            return;
          }

          const lastWord = last(prevWords);
          if (!lastWord) {
            return;
          }

          selectStartIndex = lastWord.indexWord;
          selectCurrentIndex = lastWord.indexWord;

          this.wordsToSelect.push(lastWord);
        } else {
          const nextWords = words.filter(
            (x) => x.indexLine === this.currentLineIndex + 1
          );
          if (!nextWords.length) {
            return;
          }

          const firstWord = first(nextWords);
          if (!firstWord) {
            return;
          }

          selectStartIndex = firstWord.indexWord;
          selectCurrentIndex = firstWord.indexWord;

          this.wordsToSelect.push(firstWord);
        }
      }
    }
    this.onRefreshCanvas();

    return;
  }

  if (!this.currentWord) {
    return;
  }

  const startWord = first(this.wordsToSelect);
  const endWord = last(this.wordsToSelect);
  const isDown = key === 'SELECTDOWN';

  // un seul mot sélectionné : on prend le reste des mots de la ligne,
  // ou si on y est déjà, on passe directement à la ligne suivante
  if (startWord.indexWord === endWord.indexWord) {
    let wordsToSelect = getWordsToSelect(
      words,
      startWord.indexWord,
      startWord.indexLine,
      startWord.indexLine,
      isDown
    );

    if (wordsToSelect.length === this.wordsToSelect.length) {
      const nextLineIndex = isDown
        ? startWord.indexLine + 1
        : startWord.indexLine - 1;

      wordsToSelect = getWordsToSelect(
        words,
        startWord.indexWord,
        isDown ? startWord.indexLine : nextLineIndex,
        isDown ? nextLineIndex : startWord.indexLine,
        isDown
      );
    }

    this.wordsToSelect = wordsToSelect;
  } else {
    this.wordsToSelect = extendOrShrinkByLine(
      words,
      this.wordsToSelect,
      this.currentWord,
      key
    );
  }

  if (selectStartIndex < selectCurrentIndex) {
    selectCurrentIndex = last(this.wordsToSelect).indexWord;
  } else {
    selectCurrentIndex = first(this.wordsToSelect).indexWord;
  }

  this.onRefreshCanvas();
}

// on a déjà sélectionné au-delà de la ligne de l'ancre (this.currentWord) :
// une pression continue d'une ligne entière dans le même sens,
// une pression dans l'autre sens réduit la sélection d'une ligne
// (ou mot par mot une fois revenu sur la ligne de l'ancre)
function extendOrShrinkByLine(words, wordsToSelect, anchorWord, key) {
  const startWord = first(wordsToSelect);
  const endWord = last(wordsToSelect);
  const isDown = key === 'SELECTDOWN';
  const isDownType = startWord.indexWord === anchorWord.indexWord;

  if (isDownType === isDown) {
    const targetLine = isDown ? endWord.indexLine + 1 : startWord.indexLine - 1;

    return getWordsToSelect(
      words,
      anchorWord.indexWord,
      isDown ? anchorWord.indexLine : targetLine,
      isDown ? targetLine : anchorWord.indexLine,
      isDown
    );
  }

  if (isDownType) {
    if (endWord.indexLine === anchorWord.indexLine) {
      const shrunk = wordsToSelect.slice(0, -1);
      return shrunk.length ? shrunk : [anchorWord];
    }

    return getWordsToSelect(
      words,
      anchorWord.indexWord,
      anchorWord.indexLine,
      endWord.indexLine - 1,
      true
    );
  }

  if (startWord.indexLine === anchorWord.indexLine) {
    const shrunk = wordsToSelect.slice(1);
    return shrunk.length ? shrunk : [anchorWord];
  }

  return getWordsToSelect(
    words,
    anchorWord.indexWord,
    startWord.indexLine + 1,
    anchorWord.indexLine,
    false
  );
}

function getWordsToSelect(
  words,
  startIndex,
  startLineIndex,
  endLineIndex,
  isGreaterThanEqual
) {
  return words.filter(
    (x) =>
      x.y !== 0 &&
      (isGreaterThanEqual
        ? x.indexWord >= startIndex
        : x.indexWord <= startIndex) &&
      startLineIndex <= x.indexLine &&
      x.indexLine <= endLineIndex
  );
}

// ***************************************************************************************
// onBackspaceLeftToRight supprime un caractère devant pour les mots en arabe / hébreu
// ***************************************************************************************
export function onBackspaceLeftToRight() {
  if (this.underSelection) {
    this.onDeleteSelection('onBackspaceLeftToRight');
    return;
  }

  this.addNewActionForUndo('onBackspaceLeftToRight');

  const word = this.currentWord;
  if(!word) {
    return;
  }

  let textUpdated = '';
  let i = 0;
  for (const ch of reverse(Array.from(word.text))) {
    if (i === this.currentLetter.index) {
      i++;
      continue;
    }

    textUpdated = ch + textUpdated;
    i++;
  }

  word.text = textUpdated;

  // on supprime le mot
  if (!word.text.length) {
    this.currentLine.words.splice(word.index, 1);

    const lastWord = last(this.currentLine.words);

    this.currentLineIndex = this.currentLine.index;
    this.currentWordIndex = lastWord ? lastWord.indexWord : 0;
    this.currentLetterIndex =
      lastWord && lastWord.letters.length
        ? last(lastWord.letters).indexLetter
        : 0;
  }

  this.onChange();
}

// ***************************************************************************************
// onDeleteLeftToRight permet de supprimer un caractère en arrière pour les mots en arabe / hébreu
// ***************************************************************************************
export function onDeleteLeftToRight() {
  if (this.underSelection) {
    this.onDeleteSelection('onDeleteLeftToRight');
    return;
  }

  this.addNewActionForUndo('onBackspaceLeftToRight');

  const word = this.currentWord;
  if (!word) {
    return;
  }

  let indexToDelete = this.currentLetter.index - 1;
  if (indexToDelete === -1) {
    indexToDelete = 0;
  }
  let textUpdated = '';
  let i = 0;

  for (const ch of reverse(Array.from(word.text))) {
    if (i === indexToDelete) {
      i++;
      continue;
    }

    textUpdated = ch + textUpdated;
    i++;
  }

  word.text = textUpdated;

  // on supprime le mot
  if (!word.text.length) {
    this.currentLine.words.splice(word.index, 1);

    const lastWord = last(this.currentLine.words);

    this.currentLineIndex = this.currentLine.index;
    this.currentWordIndex = lastWord ? lastWord.indexWord : 0;
    this.currentLetterIndex =
      lastWord && lastWord.letters.length
        ? last(lastWord.letters).indexLetter
        : 0;
  }

  this.onChange();
}

// ***************************************************************************************
// onDeleteWord permet de supprimer les mots sélectionnés
// ***************************************************************************************
export function onDeleteWord() {
  if (!this.currentLine.words.length) {
    return;
  }

  // Faire l'historique
  this.addNewActionForUndo('onDeleteWord');

  if (this.currentWordIndex === 0) {
    const nextWord = this.currentLine.words[this.currentWordIndex + 1];
    if (nextWord) {
      nextWord.in = this.currentWord.in;
    }
  } else {
    const prevWord = this.currentLine.words[this.currentWordIndex - 1];
    if (prevWord) {
      prevWord.out = this.currentWord.out;
    }

    this.currentWordIndex--;
  }

  this.currentLine.words.splice(this.currentWord.index, 1);

  this.onChange();
}

// ***************************************************************************************
// onDeleteWordsUntil permet de supprimer les mots du début de la ligne jusqu'au mot courant
// ***************************************************************************************
export function onDeleteWordsUntil() {
  const words = this.getWords();
  if (!words.length) {
    return;
  }

  // Faire l'historique
  this.addNewActionForUndo('onDeleteWordsUntil');

  for (const line of this.subtitle.lines) {
    const wordsToDelete = line.words.filter(
      (x) => x.indexWord < this.currentWord.indexWord
    );

    if (wordsToDelete.length) {
      const firstWord = first(wordsToDelete);

      this.currentWord.in = firstWord.in;

      line.words.splice(firstWord.index, wordsToDelete.length);
    }
  }

  this.currentWordIndex = 0;
  this.currentLetterIndex = this.currentLetter ? this.currentLetter.index : 0;

  this.onChange();
}

// ***************************************************************************************
// onDeleteLine permet de supprimer la ligne
// ***************************************************************************************
export function onDeleteLine() {

  if (!this.subtitle.lines.length){
    return;
  }

  // Faire l'historique
  this.addNewActionForUndo('onDeleteLine');

  if (this.subtitle.lines.length === 1) {
    this.currentLine.words = [];
    this.onChange();
    return;
  }

  // Update timecode
  const inTime = first(this.currentLine.words).in;
  const outTime = first(this.currentLine.words).out;

  if (this.currentLineIndex === 0) {
    const nextLine = this.subtitle.lines[this.currentLineIndex + 1];
    const firstWord = first(nextLine.words);
    firstWord.in = inTime;
  } else {
    const prevLine = this.subtitle.lines[this.currentLineIndex - 1];
    const lastWord = last(prevLine.words);
    lastWord.out = outTime;
  }

  this.subtitle.lines.splice(this.currentLineIndex, 1);

  const currentLine = this.subtitle.lines[0];
  if (currentLine.words.length) {
    const lastWord = last(currentLine.words);
    const lastLetter = last(lastWord.letters);

    this.currentLineIndex = 0;
    this.currentWordIndex = lastWord.index;
    this.currentLetterIndex = lastLetter.indexLetter;
  }

  this.onChange();
}

// ***************************************************************************************
// onDeleteSelection permet de supprimer les mots sélectionnés
// ***************************************************************************************
export function onDeleteSelection(action) {
  const words = this.getWords();
  if (!words.length) {
    return;
  }

  if (this.lettersToSelect.length) {
    // Historiser l'action
    this.addNewActionForUndo(action);

    const groupByWord = groupBy(
      this.lettersToSelect,
      (x) => `${x.indexLine}_${x.indexWord}`
    );

    const keys = Object.keys(groupByWord);

    const wordsSelected = this.subtitle.lines
      .flatMap((x) => x.words)
      .filter((x) => keys.includes(`${x.indexLine}_${x.index}`));

    let newText = '';

    keys.forEach((key) => {
      const indexs = key.split('_');
      const indexLine = first(indexs);
      const indexWord = last(indexs);

      const word = this.subtitle.lines[indexLine].words[indexWord];
      if (word) {
        const values = groupByWord[key];
        const firstLetter = first(values);

        word.text =
          word.text.substring(0, firstLetter.index) +
          word.text.substring(firstLetter.index + values.length);

        if (word.text.length) {
          newText += word.text;
        }
      }
    });

    const firstWord = cloneDeep(first(wordsSelected));
    const lastWord = cloneDeep(last(wordsSelected));

    for (const word of reverse(wordsSelected)) {
      this.subtitle.lines[word.indexLine].words.splice(word.index, 1);
    }

    const newWord = {
      ...firstWord,
      text: newText,
      out: lastWord.out,
      letters: [],
    };

    this.subtitle.lines[newWord.indexLine].words.splice(
      newWord.index,
      0,
      newWord
    );

    const startLetter = first(this.lettersToSelect);

    // On place le curseur sur le mot
    this.currentWordIndex = newWord.index;
    this.currentLineIndex = newWord.indexLine;
    this.currentLetterIndex = startLetter.indexLetter;

    // On déselectionne
    this.underSelection = false;
    this.lettersToSelect = [];

    this.onChange();

    return;
  }

  let firstWord = first(this.wordsToSelect);
  if (!firstWord) {
    return;
  }

  let prevWord = words[firstWord.indexWord - 1];
  // si le mot est vide, on va aussi le supprimer
  if (prevWord?.text === '') {
    this.wordsToSelect.unshift(prevWord);
    firstWord = prevWord;

    prevWord = words[prevWord.indexWord - 1];
  }

  // Historiser l'action
  this.addNewActionForUndo(action);

  // On supprime les mots de la liste

  this.wordsToSelect.forEach((wordToDelete) => {
    const line = this.subtitle.lines.find(
      (x) => x.index === wordToDelete.indexLine
    );
    line.words = line.words.filter((x) => x.index !== wordToDelete.index);
  });

  // On déselectionne
  this.underSelection = false;
  this.wordsToSelect = [];

  if (prevWord) {
    prevWord.endTime = firstWord.startTime;
    this.currentWordIndex = prevWord.index;
    this.currentLineIndex = prevWord.indexLine;
    this.currentLetterIndex = last(prevWord.letters).indexLetter;
  } else {
    this.currentWordIndex = 0;
    this.currentLineIndex = 0;
    this.currentLetterIndex = 0;
  }

  this.onChange();
}

// ***************************************************************************************
// onSelectWithCtrlA
// Première fois, sélectionne le mot, deuxième fois, la ligne, troisième fois tout le texte.
// ***************************************************************************************
let nbCtrlA = 0;
export function onSelectWithCtrlA() {
  const words = this.getWords();
  if (!words.length) {
    return;
  }

  if (!this.underSelection || nbCtrlA === 3) {
    this.underSelection = true;
    this.wordsToSelect = [];
    nbCtrlA = 0;
  }

  nbCtrlA++;

  if (nbCtrlA === 1) {
    this.wordsToSelect.push(this.currentWord);
  } else {
    if (nbCtrlA === 2) {
      this.wordsToSelect = this.currentLine.words;
    } else {
      this.wordsToSelect = words;
    }
  }

  this.onRefreshCanvas();
}

// ***************************************************************************************
// onMarkWord permet de marquer un mot
// ***************************************************************************************
export function onMarkWord(key) {
  if (!this.subtitle) {
    return;
  }

  const markWord = getMarkWord(key);

  const keys = findMarkWords(this.subtitle.mark);

  if (keys.includes(markWord)) {
    this.subtitle.mark -= markWord.value;
  } else {
    this.subtitle.mark += markWord.value;
  }

  this.onChange();
}

// ***************************************************************************************
// On copie
// ***************************************************************************************
export function onCopy() {
  if (this.wordsToSelect.length) {
    this.wordsSelected = cloneDeep(this.wordsToSelect);

    // Copier dans le presse-papiers
    copyToClipboard(this.wordsSelected);
  }

  if (this.lettersToSelect.length) {
    const stringToCopy = join(
      this.lettersToSelect.map((x) => x.letter),
      ''
    );

    // Copier dans le presse-papiers
    writeClipboard(stringToCopy);
  }
}

// ***************************************************************************************
// On coupe
// ***************************************************************************************
export function onCut() {
  if (!this.wordsToSelect.length) {
    return;
  }

  this.wordsSelected = [...this.wordsToSelect];

  this.onDeleteSelection('onCut');

  // Copier dans le presse-papiers
  copyToClipboard(this.wordsSelected);
}

// ***************************************************************************************
// On colle
// ***************************************************************************************
export function onPaste() {
  const currentSentence = this.getCurrentSentence().substring(
    this.currentLetterIndex
  );

  // Si on dépasse X caractères, on ne peut pas coller
  const maxLength = this.getMaxLength(this.currentLine);
  if (currentSentence.length > maxLength) {
    return;
  }

  if (this.wordsSelected.length) {
    this.pasteWords(this.wordsSelected, false);
  } else {
    // La lecture a été démarrée dans le handler keydown (contexte du geste),
    // car readText() exige focus + activation au moment de l'appel.
    const read =
      takeClipboardRead() ||
      navigator.clipboard.readText().then(
        (text) => text,
        () => null
      );
    read.then((clipText) => {
      if (clipText != null) {
        this.pasteWords(clipText.split(' '), true);
      }
    });
  }
}

// ***************************************************************************************
// onSelectUpOrDownSentence permet de sélectionner jusqu'au début ou la fin du sous-titre
// ***************************************************************************************
export function onSelectUpOrDownSentence(key) {
  this.underSelection = true;
  this.wordsToSelect = [];

  let selectStartIndex = 0;
  let selectEndIndex = 0;

  const words = this.getWords();
  if (!words.length) {
    return;
  }

  if (key === 'SELECTUPSENTENCE') {
    selectStartIndex = 0;
    selectEndIndex = this.currentWord.indexWord;
  } else {
    selectStartIndex = this.currentWord.indexWord;
    selectEndIndex = last(words).indexWord;
  }

  this.wordsToSelect = words.filter(
    (x) => x.indexWord >= selectStartIndex && x.indexWord <= selectEndIndex
  );

  this.onRefreshCanvas();
}

// ***************************************************************************************
// onSelectLetterByLetter permet de sélectionner lettre par lettre
// ***************************************************************************************
let selectCurrentWordIndex = 0;
let selectCurrentLetterIndex = 0;
let selectCurrentLineIndex = 0;

let selectCurrentDirection = '';
export function onSelectLetterByLetter(key) {
  if (!this.underSelection) {
    if (!this.currentWord || !this.currentWord.text) {
      return;
    }

    this.underSelection = true;
    this.wordsToSelect = [];
    this.lettersToSelect = [];

    selectCurrentWordIndex = this.currentWord.index;
    selectCurrentLetterIndex = this.currentLetter.index;
    selectCurrentLineIndex = this.currentLine.index;
    selectCurrentDirection = key;

    if (key === 'SELECTLETTERBYLETTERONRIGHT') {
      this.lettersToSelect.push(this.currentLetter);
      this.onRefreshCanvas();
      return;
    }
  }

  if (selectCurrentDirection === key) {
    if (key === 'SELECTLETTERBYLETTERONLEFT') {
      if (selectCurrentLetterIndex === 0) {
        if (selectCurrentWordIndex === 0) {
          if (selectCurrentLineIndex === 0) {
            return;
          }

          selectCurrentLineIndex--;
          selectCurrentWordIndex =
            this.subtitle.lines[selectCurrentLineIndex].words.length - 1;
        } else {
          selectCurrentWordIndex--;
        }

        selectCurrentLetterIndex =
          this.subtitle.lines[selectCurrentLineIndex].words[
            selectCurrentWordIndex
          ].letters.length - 1;
      } else {
        selectCurrentLetterIndex--;
      }
    } else {
      const line = this.subtitle.lines[selectCurrentLineIndex];
      const word = line.words[selectCurrentWordIndex];

      if (selectCurrentLetterIndex === word.letters.length - 1) {
        if (selectCurrentWordIndex === line.words.length - 1) {
          if (selectCurrentLineIndex === this.subtitle.lines.length - 1) {
            return;
          }

          selectCurrentLineIndex++;
          selectCurrentWordIndex = 0;
        } else {
          selectCurrentWordIndex++;
        }

        selectCurrentLetterIndex = 0;
      } else {
        // si c'est le dernier mot de la dernière ligne alors on ne doit pas sélectionner l'espace
        if (
          selectCurrentWordIndex === line.words.length - 1 &&
          selectCurrentLineIndex === this.subtitle.lines.length - 1 &&
          selectCurrentLetterIndex === word.letters.length - 2
        ) {
          return;
        }

        selectCurrentLetterIndex++;
      }
    }
  } else {
    // on change de direction
    selectCurrentDirection = key;
  }

  const word =
    this.subtitle.lines[selectCurrentLineIndex]?.words[selectCurrentWordIndex];
  const letter = word?.letters[selectCurrentLetterIndex];

  if (letter) {
    // Si la lettre est déjà sélectionnée, on la déselectionne
    const indexToRemove = this.lettersToSelect.findIndex(
      (l) =>
        l.index === letter.index &&
        l.indexWord === letter.indexWord &&
        l.indexLetter === letter.indexLetter &&
        l.indexLine === letter.indexLine
    );
    if (indexToRemove !== -1) {
      this.lettersToSelect.splice(indexToRemove, 1);
    } else {
      if (key === 'SELECTLETTERBYLETTERONLEFT') {
        this.lettersToSelect.unshift(letter);
      } else {
        this.lettersToSelect.push(letter);
      }
    }

    this.currentLineIndex = word.indexLine;
    this.currentWordIndex = word.index;
    this.currentLetterIndex = letter.indexLetter;
  }

  this.onRefreshCanvas();
}

// ***************************************************************************************
// onTab permet de corriger le mot quand on fait l'action Tab
// ***************************************************************************************
export function onTab() {

  const commonErrors = this.commonErrors || this.props.commonErrors;

  if (!commonErrors) {
    return;
  }

  if (!this.currentWord || !this.currentWord.text.length) {
    return;
  }

  // Faire l'historique
  this.addNewActionForUndo('onTab');

  let originalText;
  let currentText;

  const regex = /[!?.,;:()\\[\]\\]/g;

  if (this.underSelection) {
    originalText = join(
      this.wordsToSelect.map((x) => x.text),
      ' '
    );

    currentText = originalText.replace(regex, '');
  } else {
    originalText = this.currentWord.text;
    currentText = this.currentWord.text.replace(regex, '');
  }

  const wordToChange = commonErrors.find((x) =>
    x.some((word) => word.toLowerCase() === currentText.toLowerCase())
  );

  if (wordToChange) {
    let index = wordToChange.findIndex(
      (x) => x.toLowerCase() === currentText.toLowerCase()
    );

    if (index === wordToChange.length - 1) {
      index = 0;
    } else {
      index++;
    }

    let newText = originalText.replace(
      currentText,
      isLetterUpperCase(currentText[0])
        ? upperFirst(wordToChange[index])
        : wordToChange[index]
    );

    if (this.underSelection) {
      const firstWord = first(this.wordsToSelect);
      const lastWord = last(this.wordsToSelect);

      /*  if (firstWord.index === 0) {
        newText = upperFirst(newText);
      }*/

      const newWord = {
        ...firstWord,
        text: newText,
        in: firstWord.in,
        out: lastWord.out,
        letters: [],
      };

      this.currentLine.words.splice(
        firstWord.index,
        this.wordsToSelect.length,
        newWord
      );

      this.currentWordIndex = firstWord.index;

      this.underSelection = false;

      this.onChange();

      return;
    }

    this.currentWord.text = newText;

    this.onChange();
  }
}

export function onChangeLineHeight() {
  if (!this.currentLine) {
    return;
  }
  this.addNewActionForUndo('onChangeLineHeight');
  const { doubleHeight } = this.props.subtitleParameters;
  this.currentLine.doubleHeight = !(this.currentLine.doubleHeight ?? doubleHeight);
  this.onChange();
}